import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getTrainingSettings, updateTrainingSettings } from './api';

// Which class durations ZU actually runs. In the database rather than in code
// because it is a business fact that changes without a deploy: when it was
// hardcoded, a length the catalog really offers got rounded away as though the AI
// had made it up.
export default function ClassLengthSettings() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({ queryKey: ['training-settings'], queryFn: getTrainingSettings, retry: false });
  const [form, setForm] = useState({
    elearning_length_minutes: '', live_length_minutes: '',
    narration_wpm: '', knowledge_check_questions: '',
    knowledge_check_seconds_per_question: '', default_script_format: '',
  });
  // Seed once: the app refetches on window focus, and re-seeding on every data
  // change would wipe a list being typed after an alt-tab.
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current || !data?.settings) return;
    seeded.current = true;
    setForm({
      elearning_length_minutes: data.settings.elearning_length_minutes || '',
      live_length_minutes: data.settings.live_length_minutes || '',
      narration_wpm: data.settings.narration_wpm || '',
      knowledge_check_questions: data.settings.knowledge_check_questions || '',
      knowledge_check_seconds_per_question: data.settings.knowledge_check_seconds_per_question || '',
      default_script_format: data.settings.default_script_format || '',
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => updateTrainingSettings({
      elearning_length_minutes: form.elearning_length_minutes.trim(),
      live_length_minutes: form.live_length_minutes.trim(),
      narration_wpm: String(form.narration_wpm).trim(),
      knowledge_check_questions: String(form.knowledge_check_questions).trim(),
      knowledge_check_seconds_per_question: String(form.knowledge_check_seconds_per_question).trim(),
      default_script_format: form.default_script_format,
    }),
    onSuccess: () => {
      toast.success('Class settings saved');
      qc.invalidateQueries({ queryKey: ['training-settings'] });
    },
    onError: (e) => toast.error(e?.message || 'Could not save'),
  });

  if (isLoading) return <div className="text-xs text-gray-500">Loading...</div>;
  if (isError) return <div className="text-xs text-red-500">Could not load class lengths.</div>;

  const field = 'w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 mt-1';
  const defaults = data?.length_defaults || {};
  const effective = data?.length_effective || {};
  const list = (v) => (Array.isArray(v) ? v.join(', ') : '');
  const narrationDefaults = data?.narration_defaults || {};
  const narrationEffective = data?.narration_effective || {};

  return (
    <div className="max-w-[620px]">
      <h3 className="text-sm font-semibold mb-1">Class lengths</h3>
      <p className="text-xs text-gray-500 mb-4">
        The durations you actually run. JPRO recommends a length for every concept and
        it is snapped to the nearest one on this list, so a duration you cannot deliver
        never reaches scheduling or the catalog. Add or remove values as your offering
        changes. Ties round up, since a class can finish early but cannot borrow time.
      </p>

      <label className="block text-xs text-gray-500 mb-3">
        eLearning (minutes, comma separated)
        <input value={form.elearning_length_minutes} className={field}
          placeholder={list(defaults.elearning)}
          onChange={(e) => setForm((f) => ({ ...f, elearning_length_minutes: e.target.value }))} />
        <span className="text-[11px] text-gray-400">In use now: {list(effective.elearning) || 'none'}</span>
      </label>

      <label className="block text-xs text-gray-500 mb-3">
        Live (minutes, comma separated)
        <input value={form.live_length_minutes} className={field}
          placeholder={list(defaults.live)}
          onChange={(e) => setForm((f) => ({ ...f, live_length_minutes: e.target.value }))} />
        <span className="text-[11px] text-gray-400">In use now: {list(effective.live) || 'none'}</span>
      </label>

      <p className="text-[11px] text-gray-400 mb-4">
        Leave a field blank to fall back to {list(defaults.elearning)} for eLearning and{' '}
        {list(defaults.live)} for live. Changing this affects concepts generated from now
        on; it does not rewrite lengths already recommended.
      </p>

      <h3 className="text-sm font-semibold mb-1 mt-6">Narration budget</h3>
      <p className="text-xs text-gray-500 mb-4">
        A class's runtime is spoken words divided by a speaking rate. JPRO uses these to
        work out how much narration a class of a given length needs, and splits that
        across its chapters. A knowledge check takes clock time without narration, so its
        seconds come off the budget before the split.
      </p>

      <label className="block text-xs text-gray-500 mb-3">
        Narration rate (words per minute)
        <input type="number" min="60" max="300" value={form.narration_wpm} className={field}
          placeholder={String(narrationDefaults.narration_wpm ?? '')}
          onChange={(e) => setForm((f) => ({ ...f, narration_wpm: e.target.value }))} />
        <span className="text-[11px] text-gray-400">
          In use now: {narrationEffective.narration_wpm} wpm
        </span>
      </label>

      <label className="block text-xs text-gray-500 mb-3">
        Questions per knowledge check
        <input type="number" min="1" max="20" value={form.knowledge_check_questions} className={field}
          placeholder={String(narrationDefaults.knowledge_check_questions ?? '')}
          onChange={(e) => setForm((f) => ({ ...f, knowledge_check_questions: e.target.value }))} />
        <span className="text-[11px] text-gray-400">
          In use now: {narrationEffective.knowledge_check_questions}
        </span>
      </label>

      <label className="block text-xs text-gray-500 mb-3">
        Seconds a learner spends per knowledge check question
        <input type="number" min="5" max="300" value={form.knowledge_check_seconds_per_question}
          className={field}
          placeholder={String(narrationDefaults.knowledge_check_seconds_per_question ?? '')}
          onChange={(e) => setForm((f) => ({ ...f, knowledge_check_seconds_per_question: e.target.value }))} />
        <span className="text-[11px] text-gray-400">
          In use now: {narrationEffective.knowledge_check_seconds_per_question} seconds
        </span>
      </label>

      <label className="block text-xs text-gray-500 mb-4">
        Script format preselected for a new class
        <select value={form.default_script_format} className={field}
          onChange={(e) => setForm((f) => ({ ...f, default_script_format: e.target.value }))}>
          <option value="">Use the standard default</option>
          {(data?.script_formats || []).map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <span className="text-[11px] text-gray-400">
          In use now: {narrationEffective.default_script_format}. An author can change it
          per class before producing.
        </span>
      </label>

      <button onClick={() => save.mutate()} disabled={save.isPending}
        className="text-sm font-bold px-4 py-2 rounded-lg bg-orange-500 text-white disabled:opacity-40">
        {save.isPending ? 'Saving…' : 'Save class settings'}
      </button>
    </div>
  );
}
